import { api } from '@/api/client'
import type { ConnectionStatus, ProductUnit } from '@/api/suppliers'

export interface Category {
  id: string
  name: string
  sortOrder: number
  productCount: number
}

/** Ένας προμηθευτής που φέρνει το προϊόν. */
export interface ProductSupplier {
  businessItemId: string
  supplierId: string
  supplierName: string
  connectionStatus: ConnectionStatus
  price: string | null
  isAvailableToday: boolean
  unavailableUntil: string | null
}

/**
 * Ένα προϊόν όπως το βλέπει ο χρήστης.
 *
 * Στη βάση είναι μία εγγραφή ανά προμηθευτή· οι αλλαγές στέλνουν όλα τα
 * `businessItemId` μαζί, ώστε να μένουν ίδιες σε όλους.
 */
export interface Product {
  key: string
  name: string
  unit: ProductUnit
  categoryId: string | null
  note: string | null
  defaultQuantity: string
  isFavorite: boolean
  suppliers: ProductSupplier[]
}

export interface Products {
  categories: Category[]
  products: Product[]
}

export interface ProductInput {
  name: string
  unit: ProductUnit
  categoryId: string | null
  note: string
}

export const itemIds = (product: Product) => product.suppliers.map((s) => s.businessItemId)

export const productsApi = {
  list: () => api.get<Products>('/products'),
  create: (payload: ProductInput & { supplierIds: string[] }) =>
    api.post<Products>('/products', payload),
  update: (product: Product, payload: Partial<ProductInput> & { isFavorite?: boolean }) =>
    api.patch<Products>('/products', { itemIds: itemIds(product), ...payload }),
  addSupplier: (product: Product, supplierId: string) =>
    api.post<Products>('/products/suppliers', {
      itemId: product.suppliers[0].businessItemId,
      supplierId,
    }),
  removeSupplier: (businessItemId: string) =>
    api.post<Products>('/products/delete', { itemIds: [businessItemId] }),
  remove: (product: Product) =>
    api.post<Products>('/products/delete', { itemIds: itemIds(product) }),

  createCategory: (name: string) => api.post<Category>('/categories', { name }),
  renameCategory: (id: string, name: string) => api.patch<Products>(`/categories/${id}`, { name }),
  deleteCategory: (id: string) => api.delete<Products>(`/categories/${id}`),
  reorderCategories: (ids: string[]) => api.put<Products>('/categories/order', { ids }),
}
